/**
 * Hotel-specific content generation.
 * Produces deterministic but varied amenities, reviews, and gallery
 * per hotel, so each property detail page feels unique.
 */

import {
  Wifi, Car, UtensilsCrossed, Dumbbell, Waves, Wind, Coffee, Shield,
  Tv, Bath, Flower2, Landmark, Bike, Plane, Baby, Music, Accessibility, Glasses,
  type LucideIcon,
} from 'lucide-react';

/* ── Types ─────────────────────────────────────────────────────────── */

export interface HotelAmenityItem { icon: LucideIcon; label: string }
export interface HotelReviewItem { initials: string; name: string; date: string; rating: number; verified: boolean; text: string }

/* ── Full amenity pool (18 items) ──────────────────────────────────── */

const AMENITY_POOL: HotelAmenityItem[] = [
  { icon: Wifi, label: 'High-Speed Wi-Fi' },
  { icon: Car, label: 'Valet Parking' },
  { icon: UtensilsCrossed, label: 'Fine Dining' },
  { icon: Dumbbell, label: 'Fitness Centre' },
  { icon: Waves, label: 'Infinity Pool' },
  { icon: Wind, label: 'Climate Control' },
  { icon: Coffee, label: 'Lounge & Bar' },
  { icon: Shield, label: '24/7 Security' },
  { icon: Tv, label: 'Smart TV' },
  { icon: Bath, label: 'Luxury Bath' },
  { icon: Flower2, label: 'Spa & Wellness' },
  { icon: Landmark, label: 'Heritage Tours' },
  { icon: Bike, label: 'Bicycle Rental' },
  { icon: Plane, label: 'Airport Transfer' },
  { icon: Baby, label: 'Kids Club' },
  { icon: Music, label: 'Live Entertainment' },
  { icon: Accessibility, label: 'Accessible Rooms' },
  { icon: Glasses, label: 'Business Centre' },
];

/* ── Full review pool (24 reviews) ─────────────────────────────────── */

const REVIEW_POOL: Omit<HotelReviewItem, 'text'>[] = [
  { initials: 'AK', name: 'Arjun Kapoor', date: 'February 2026', rating: 5, verified: true },
  { initials: 'SM', name: 'Sneha Menon', date: 'January 2026', rating: 4, verified: true },
  { initials: 'RD', name: 'Rahul Deshmukh', date: 'December 2025', rating: 5, verified: true },
  { initials: 'PJ', name: 'Priya Joshi', date: 'March 2026', rating: 5, verified: true },
  { initials: 'VK', name: 'Vikram Kumar', date: 'November 2025', rating: 3, verified: false },
  { initials: 'NS', name: 'Nisha Sharma', date: 'January 2026', rating: 4, verified: true },
  { initials: 'AT', name: 'Aditya Thakur', date: 'October 2025', rating: 2, verified: false },
  { initials: 'MR', name: 'Meera Reddy', date: 'March 2026', rating: 5, verified: true },
  { initials: 'DM', name: 'Deepak Malhotra', date: 'February 2026', rating: 4, verified: true },
  { initials: 'KR', name: 'Kavita Rao', date: 'January 2026', rating: 5, verified: true },
  { initials: 'SG', name: 'Suresh Gupta', date: 'December 2025', rating: 3, verified: true },
  { initials: 'RP', name: 'Ritu Patel', date: 'November 2025', rating: 4, verified: true },
  { initials: 'AN', name: 'Amit Nair', date: 'March 2026', rating: 5, verified: true },
  { initials: 'LS', name: 'Lakshmi Srinivasan', date: 'February 2026', rating: 4, verified: false },
  { initials: 'MT', name: 'Manish Tewari', date: 'January 2026', rating: 5, verified: true },
  { initials: 'PA', name: 'Pooja Agarwal', date: 'December 2025', rating: 3, verified: true },
  { initials: 'RJ', name: 'Rajesh Jain', date: 'March 2026', rating: 4, verified: true },
  { initials: 'NG', name: 'Neha Goel', date: 'February 2026', rating: 5, verified: true },
  { initials: 'BK', name: 'Bhavesh Khanna', date: 'November 2025', rating: 4, verified: true },
  { initials: 'SK', name: 'Simran Kaur', date: 'January 2026', rating: 5, verified: true },
  { initials: 'YP', name: 'Yash Pandey', date: 'December 2025', rating: 3, verified: false },
  { initials: 'TC', name: 'Tanvi Choudhary', date: 'March 2026', rating: 4, verified: true },
  { initials: 'HB', name: 'Harsh Bhatt', date: 'February 2026', rating: 5, verified: true },
  { initials: 'DS', name: 'Divya Sen', date: 'January 2026', rating: 4, verified: true },
];

/* ── Review text templates (per star level) ────────────────────────── */

const REVIEW_TEXTS: Record<number, string[]> = {
  5: [
    'Absolutely spectacular stay at {hotel}. The suite was immaculate, and the staff went above and beyond. The {amenity} was world-class.',
    'Our third visit to {hotel} and it keeps getting better. The {location} setting is breathtaking. Already planning our next trip.',
    'This is what {stars}-star hospitality looks like. From arrival to departure, everything at {hotel} was flawless. The {amenity} alone is worth the visit.',
    '{hotel} exceeded every expectation. The room overlooking {location} was stunning, and the in-room dining rivals the best restaurants in the city.',
    'Perfect getaway at {hotel}. The {amenity} was mesmerizing, and the staff remembered our preferences from the last visit. Truly personalized service.',
  ],
  4: [
    'Beautiful property with excellent amenities. The {amenity} at {hotel} was one of the best experiences. Minor wait at breakfast, but food quality more than compensated.',
    'Very enjoyable stay at {hotel}. Loved the {location} atmosphere. The rooms are well-appointed and the {amenity} is a real highlight.',
    'Great experience at {hotel}. The concierge helped plan our entire itinerary around {location}. Comfortable rooms and friendly staff throughout.',
    '{hotel} delivers solid {stars}-star quality. The {amenity} was impressive. Room service was prompt and the overall ambiance is elegant.',
  ],
  3: [
    'Decent stay at {hotel}. The room was clean and well-maintained. However, the check-in process took longer than expected. Good {location} location though.',
    'Mixed experience at {hotel}. The {amenity} was nice but the room was smaller than expected. Breakfast variety could improve. Convenient {location} location.',
    'Average for the price point at {hotel}. The {location} area is lovely, and the {amenity} was serviceable. Could use some updates to common areas.',
  ],
  2: [
    'Expected more from {hotel} given the {stars}-star branding. Room was smaller than advertised and the {amenity} needs maintenance. {location} area is the main draw.',
  ],
};

/* ── Seeded selection (deterministic per hotel) ────────────────────── */

function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function seededShuffle<T>(arr: T[], seed: number): T[] {
  const out = [...arr];
  let s = seed;
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 16807 + 7) % 2147483647;
    const j = s % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/* ── Public API ────────────────────────────────────────────────────── */

export function getHotelAmenities(hotelName: string, stars: number): HotelAmenityItem[] {
  const seed = hashCode(hotelName);
  const count = stars >= 5 ? 10 : stars >= 4 ? 8 : 6;
  const shuffled = seededShuffle(AMENITY_POOL, seed);
  return shuffled.slice(0, count);
}

export function getHotelReviews(hotelName: string, hotelLocation: string, stars: number): HotelReviewItem[] {
  const seed = hashCode(hotelName);
  const count = stars >= 5 ? 8 : stars >= 4 ? 6 : 4;
  const shuffledReviewers = seededShuffle(REVIEW_POOL, seed).slice(0, count);
  const city = hotelLocation.split(',')[0].trim();
  const amenities = getHotelAmenities(hotelName, stars);
  const topAmenity = amenities[0]?.label || 'dining';

  return shuffledReviewers.map((reviewer, i) => {
    const rating = reviewer.rating;
    const templates = REVIEW_TEXTS[rating] || REVIEW_TEXTS[3];
    const template = templates[i % templates.length];
    const text = template
      .replace(/\{hotel\}/g, hotelName)
      .replace(/\{location\}/g, city)
      .replace(/\{stars\}/g, String(stars))
      .replace(/\{amenity\}/g, topAmenity);
    return { ...reviewer, text };
  });
}

/**
 * Placeholder gallery images (picsum.photos) for a demo property.
 *
 * `isDemo` comes from the caller's `useTenantContext()` — a real property's
 * gallery comes from its own uploads, so this returns nothing outside demo.
 */
export function getHotelGallerySeeds(hotelName: string, isDemo: boolean): string[] {
  if (!isDemo) return [];
  const seed = hashCode(hotelName);
  const categories = ['lobby', 'room', 'pool', 'restaurant', 'spa', 'view', 'garden', 'suite'];
  const shuffled = seededShuffle(categories, seed);
  return shuffled.map((cat, i) =>
    `https://picsum.photos/seed/${hotelName.toLowerCase().replace(/\s+/g, '-')}-${cat}-${i}/800/600`,
  );
}
